import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, money, fmtDate, openBlob } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { BillingDialog } from "@/components/AdminBilling";
import { METHOD_LABEL, ENTRY_LABEL, coverageLabel } from "@/lib/constants";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const cell = "whitespace-nowrap p-3";

const Tbl = ({ head, children, testId }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{head.map((h) => <th key={h} className={cell}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Balances = ({ rows, reload }) => (
  <Tbl testId="admin-balances-table" head={["Policy #", "Insured", "State", "Plan", "Billed to date", "Paid", "Balance", "Next due", "Status", ""]}>
    {rows.map((r) => (
      <tr key={r.policy_id} className="border-t border-slate-100" data-testid="admin-balance-row">
        <td className={`${cell} font-mono`}>{r.policy_number}</td>
        <td className={cell}><p className="font-semibold">{r.insured_name}</p><p className="text-xs text-slate-500">{r.user_email}</p></td>
        <td className={`${cell} font-mono`}>{r.state}</td>
        <td className={cell}>{r.payment_plan === "full" ? "Paid in full" : r.autopay ? "Monthly auto-pay" : "Monthly"}</td>
        <td className={`${cell} font-mono`}>{money(r.scheduled_to_date + r.charges - r.credits)}</td>
        <td className={`${cell} font-mono`}>{money(r.paid)}</td>
        <td className={`${cell} font-mono font-semibold ${r.balance > 0 ? "text-red-700" : ""}`}>{money(r.balance)}</td>
        <td className={cell}>{r.next_due_date ? fmtDate(r.next_due_date) : "—"}</td>
        <td className={cell}><StatusPill status={r.billing_status} /></td>
        <td className={cell}><BillingDialog policy={{ id: r.policy_id, policy_number: r.policy_number, insured_name: r.insured_name }} onDone={reload} /></td>
      </tr>
    ))}
  </Tbl>
);

const Ledger = () => {
  const [f, setF] = useState({ start: "", end: "", type: "all", method: "all" });
  const [rows, setRows] = useState([]);
  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
  const load = useCallback(() => {
    api.get("/admin/accounting/ledger", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all")) })
      .then((r) => setRows(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, [f]);
  useEffect(() => {
    load();
  }, [load]);
  const exportCsv = () => openBlob(`/admin/accounting/ledger.csv?${new URLSearchParams(params())}`, "seutrade-ledger.csv", true).catch(() => toast.error("Export failed"));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label>From</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="ledger-start-input" /></div>
        <div className="space-y-1"><Label>To</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="ledger-end-input" /></div>
        <div className="w-40 space-y-1"><Label>Type</Label>
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="ledger-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{Object.entries(ENTRY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-48 space-y-1"><Label>Method</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger data-testid="ledger-method-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All methods</SelectItem>{Object.entries(METHOD_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="ledger-export-btn"><Download className="mr-2 h-4 w-4" /> Download spreadsheet</Button>
      </div>
      <Tbl testId="admin-ledger-table" head={["Date", "Policy #", "Insured", "State", "Type", "Method", "Amount", "Reference", "Note", "By"]}>
        {rows.map((e) => (
          <tr key={e.id} className="border-t border-slate-100" data-testid="admin-ledger-row">
            <td className={cell}>{new Date(e.created_at).toLocaleString()}</td>
            <td className={`${cell} font-mono`}>{e.policy_number}</td>
            <td className={cell}>{e.insured_name}</td>
            <td className={`${cell} font-mono`}>{e.state}</td>
            <td className={cell}><StatusPill status={e.type} /></td>
            <td className={cell}>{METHOD_LABEL[e.method] || e.method}</td>
            <td className={`${cell} font-mono`}>{["refund", "credit"].includes(e.type) ? "−" : ""}{money(e.amount)}</td>
            <td className={`${cell} max-w-[140px] truncate font-mono text-xs`}>{e.reference}</td>
            <td className="p-3 text-slate-500">{e.note}</td>
            <td className={`${cell} text-xs text-slate-500`}>{e.created_by}</td>
          </tr>
        ))}
      </Tbl>
      {!rows.length && <p className="text-sm text-slate-400" data-testid="ledger-empty">No entries for these filters.</p>}
    </div>
  );
};

const Breakdown = ({ title, data, label = (k) => k }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
    {Object.keys(data).length ? Object.entries(data).map(([k, v]) => (
      <div key={k} className="flex justify-between border-b border-slate-100 py-1.5 text-sm last:border-0"><span>{label(k)}</span><span className="font-mono">{money(v)}</span></div>
    )) : <p className="text-sm text-slate-400">No data</p>}
  </div>
);

const Reports = () => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/admin/accounting/report", { params: { year } }).then((res) => setR(res.data)).catch((err) => toast.error(formatApiError(err)));
  }, [year]);
  if (!r) return null;
  const chart = r.months.map((m) => ({ name: MONTHS[m.month - 1], Net: m.net, Refunds: m.refunds }));
  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32" data-testid="report-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[0, 1, 2].map((i) => <SelectItem key={i} value={String(thisYear - i)}>{thisYear - i}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={() => openBlob(`/admin/accounting/report.csv?year=${year}`, `seutrade-revenue-${year}.csv`, true).catch(() => toast.error("Export failed"))} data-testid="report-export-btn">
          <Download className="mr-2 h-4 w-4" /> Download spreadsheet
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[["Collected", r.totals.payments], ["Refunded", r.totals.refunds], ["Net revenue", r.totals.net], ["Outstanding", r.outstanding], ["Past due", r.past_due_amount]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
            <p className="mt-1 font-mono text-2xl font-semibold" data-testid={`report-${k.toLowerCase().replace(/ /g, "-")}`}>{money(v)}</p>
            {k === "Past due" && <p className="text-xs text-slate-500">{r.past_due_count} {r.past_due_count === 1 ? "policy" : "policies"}</p>}
          </div>
        ))}
      </div>
      <div className="h-72 rounded-lg border border-slate-200 bg-white p-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip formatter={(v) => money(v)} />
            <Bar dataKey="Net" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Refunds" fill="#EF4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Tbl testId="report-months-table" head={["Month", "Collected", "Refunds", "Net", "Charges", "Credits", "Waived", "Free policies", "Free value"]}>
        {r.months.map((m) => (
          <tr key={m.month} className="border-t border-slate-100">
            <td className={cell}>{MONTHS[m.month - 1]}</td>
            {["payments", "refunds", "net", "charges", "credits", "waived"].map((k) => <td key={k} className={`${cell} font-mono`}>{money(m[k])}</td>)}
            <td className={`${cell} font-mono`}>{m.comped_count}</td>
            <td className={`${cell} font-mono`}>{money(m.comped_value)}</td>
          </tr>
        ))}
      </Tbl>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="report-comped-section">
        <p className="font-semibold">Free policies & waivers in {r.year}</p>
        <p className="mt-1 text-sm text-slate-700" data-testid="report-comped-summary">{r.totals.comped_count} {r.totals.comped_count === 1 ? "policy" : "policies"} issued at no charge (full-price value {money(r.totals.comped_value)}) · {money(r.totals.waived)} waived</p>
        {!!r.comped.length && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-comped-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Policy", "Insured", "Type", "Issued", "By", "Value"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
              <tbody>{r.comped.map((c) => (
                <tr key={c.policy_number} className="border-t border-amber-200"><td className="p-2 font-mono">{c.policy_number}</td><td className="p-2">{c.insured_name}</td><td className="p-2">{coverageLabel(c.coverage_type)}</td><td className="p-2">{c.issued}</td><td className="p-2">{c.issued_by}</td><td className="p-2 font-mono">{money(c.value)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Breakdown title="Net by state" data={r.by_state} />
        <Breakdown title="Net by policy type" data={r.by_type} label={coverageLabel} />
        <Breakdown title="Net by payment method" data={r.by_method} label={(k) => METHOD_LABEL[k] || k} />
      </div>
    </div>
  );
};

export const AdminAccounting = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/accounting/balances").then((r) => setRows(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <Tabs defaultValue="balances" data-testid="admin-accounting">
      <TabsList>
        <TabsTrigger value="balances" data-testid="accounting-tab-balances">Balances & past due</TabsTrigger>
        <TabsTrigger value="ledger" data-testid="accounting-tab-ledger">Ledger</TabsTrigger>
        <TabsTrigger value="reports" data-testid="accounting-tab-reports">Revenue reports</TabsTrigger>
      </TabsList>
      <TabsContent value="balances" className="mt-4"><Balances rows={rows} reload={load} /></TabsContent>
      <TabsContent value="ledger" className="mt-4"><Ledger /></TabsContent>
      <TabsContent value="reports" className="mt-4"><Reports /></TabsContent>
    </Tabs>
  );
};
